import { LoanApiUrls } from "@/modules/loan/common/enum";
import type {
  ILoanByContractNumberRequest,
  ILoanByIdRequest,
  ILoanPenaltiesRequest,
  ILoanScheduleRequest,
  ILoansRequest,
  ILoanTransactionRequest,
  TLoanPenaltiesResponse,
  TLoanResponse,
  TLoanScheduleResponse,
  TLoansResponse,
  TLoanTransactionResponse,
} from "@/modules/loan/common/types";
import { ResponseType } from "@/shared/common/enums";
import type {
  IBlobError,
  IBlobUrlResponse,
  IStatementWithIdDownloadRequest,
  IStatementWithIdRequest,
} from "@/shared/common/types";
import { buildRoute, generateQuery } from "@/shared/helpers";
import { blobToText } from "@/shared/utils/common";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const loanApi = createApi({
  reducerPath: "loanApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getLoans: builder.query<TLoansResponse, ILoansRequest>({
      query: ({ loanTypes = [], pageSize }) => {
        let query = generateQuery({ pageSize });
        loanTypes?.forEach((type) => {
          const loanTypesQuery = generateQuery({ loanTypes: type });
          query += loanTypesQuery ? `&${loanTypesQuery}` : "";
        });
        const url = `${LoanApiUrls.Loans}?${query}`;
        return { url };
      },
    }),
    getLoanById: builder.query<TLoanResponse, ILoanByIdRequest>({
      query: ({ id }) => ({
        url: `${LoanApiUrls.Loans}/${id}`
      })
    }),
    getLoanByContractNumber: builder.query<TLoanResponse, ILoanByContractNumberRequest>({
      query: ({ loanContractNumber, loanRepaymentType, loanRepaymentAmount }) => {
        const query = generateQuery({ loanRepaymentType, loanRepaymentAmount });
        const url = `${LoanApiUrls.Loans}/${loanContractNumber}?${query}`;
        return { url };
      }
    }),
    getLoanPenalties: builder.query<TLoanPenaltiesResponse, ILoanPenaltiesRequest>({
      query: ({ contractNumber, loanRepaymentType, loanRepaymentAmount }) => {
        const query = generateQuery({ loanRepaymentType, loanRepaymentAmount });
        const url = `${LoanApiUrls.Loans}/${contractNumber}/Penalties?${query}`;
        return { url };
      }
    }),
    getLoanTransactions: builder.query<
      TLoanTransactionResponse,
      ILoanTransactionRequest
    >({
      query: ({
        id,
        pageIndex,
        pageSize,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
          startDate,
          endDate,
        });
        const url = buildRoute(
          LoanApiUrls.LoanTransactions, { id }
        );
        return { url: `${url}?${query}` };
      },
    }),
    getLoanSchedule: builder.query<
      TLoanScheduleResponse,
      ILoanScheduleRequest
    >({
      query: ({
        id,
        pageIndex,
        pageSize,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
        });
        const url = buildRoute(LoanApiUrls.LoanSchedule, { id });
        return { url: `${url}?${query}` };
      },
    }),
    getLoanStatement: builder.query<
      string,
      IStatementWithIdRequest
    >({
      query: ({
        id,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          startDate,
          endDate,
        });
        const url = buildRoute(LoanApiUrls.LoanStatement, { id });
        return {
          url: `${url}?${query}`,
        };
      }
    }),
    downloadLoanStatement: builder.query<
      IBlobUrlResponse,
      IStatementWithIdDownloadRequest
    >({
      query: ({
        id,
        exportFormat,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          exportFormat,
          startDate,
          endDate,
        });
        const url = buildRoute(LoanApiUrls.LoanStatementExport, { id });
        return {
          url: `${url}?${query}`,
          responseType: ResponseType.Blob
        };
      },
      transformResponse: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        return { url };
      },
      transformErrorResponse: async (error: IBlobError) => {
        const text = await blobToText(error) as string;
        const data = text ? JSON.parse(text) : null;

        return {
          status: error.status,
          data
        };
      }
    }),
  }),
});

export const {
  useGetLoansQuery,
  useGetLoanByIdQuery,
  useGetLoanScheduleQuery,
  useGetLoanStatementQuery,
  useGetLoanTransactionsQuery,
  useLazyGetLoanPenaltiesQuery,
  useLazyDownloadLoanStatementQuery,
  useLazyGetLoanByContractNumberQuery,
} = loanApi;
